import React, { useEffect, useMemo, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
  Pressable,
  Modal,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { DivineBackground } from "../../shared/ui/DivineBackground";
import { Header } from "../../shared/ui/Header";
import { Button, Input } from "../../shared/ui/FormElements";
import { ToastHost } from "../../shared/ui/ToastHost";
import {Flash} from "../../shared/utils/Flash";

import { AuthStackParamList } from "../../navigation/navigationTypes";
import { UserRegistrationService } from "../../services/UserRegistrationService";
import { ForgotMPINService } from "../../services/ForgotMPINService";
import { useAuth } from "../../../context/AuthContext";
import { UserService } from "../../services/UserService";
import { format } from "date-fns";
import { OtpChannelInfoModel, VerificationParameterModel } from "../../models/OtpVerificationModel";
import {
  OTP_MAX_LENGTH,
  OTP_PLACEHOLDER,
  hasAnyVerificationChannel,
  normalizeOtpChannelInfo,
  normalizeVerificationParameter,
} from "../../shared/utils/verification";
import {
  getBackendDisplayMessage,
  getBackendMessageKey,
} from "../../shared/utils/backendMessages";

/** ✅ CityId of "Other" (same as RegisterScreen) */
const OTHER_CITY_ID = "00000000-0000-0000-0000-000000000000";
const EMPTY_VERIFICATION_PARAMETER: VerificationParameterModel = {
  email: false,
  mobile: false,
};
const EMPTY_OTP_CHANNELS: OtpChannelInfoModel = {
  email: false,
  mobile: false,
  maskedEmail: null,
  maskedMobileNumber: null,
};
const DOB_PLACEHOLDER = "DD-MM-YYYY";

const formatDobInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;

  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

const parseDobInput = (value: string) => {
  const trimmed = value.trim();
  if (!/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return null;

  const [dayText, monthText, yearText] = trimmed.split("-");
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const candidate = new Date(year, month - 1, day);

  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }

  return candidate;
};

const getDobValidationError = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "Date of Birth is required.";
  }

  if (!/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    return `Enter Date of Birth in ${DOB_PLACEHOLDER} format.`;
  }

  const [dayText, monthText, yearText] = trimmed.split("-");
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const currentYear = new Date().getFullYear();

  if (month < 1 || month > 12) {
    return "Month must be between 01 and 12.";
  }

  if (day < 1 || day > 31) {
    return "Date must be between 01 and 31.";
  }

  if (year >= currentYear) {
    return `Year must be before ${currentYear}.`;
  }

  if (!parseDobInput(trimmed)) {
    return "Enter a valid calendar date.";
  }

  return "";
};

export interface SelectOption {
  id: string;
  name: string;
}

type Nav = NativeStackNavigationProp<AuthStackParamList, "EditProfile">;

/** ✅ Inline searchable dropdown (same as RegisterScreen) */
function SearchSelect(props: {
  label: string;
  required?: boolean;
  placeholder: string;
  valueLabel: string;
  valueId?: string | null;
  options: SelectOption[];
  alwaysVisibleOptionIds?: string[];
  disabled?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (opt: SelectOption) => void;
  searchPlaceholder?: string;
}) {
  const {
    label,
    required,
    placeholder,
    valueLabel,
    valueId,
    options,
    alwaysVisibleOptionIds,
    disabled,
    isOpen,
    onToggle,
    onSelect,
    searchPlaceholder,
  } = props;

  const [q, setQ] = useState("");

  useEffect(() => {
    if (!isOpen) setQ("");
  }, [isOpen]);

  const filtered = useMemo(() => {
    const pinnedIds = new Set(
      (alwaysVisibleOptionIds ?? []).map((id) => String(id).toLowerCase())
    );
    const pinnedOptions = options.filter((x) =>
      pinnedIds.has(String(x.id).toLowerCase())
    );
    const searchableOptions = options.filter(
      (x) => !pinnedIds.has(String(x.id).toLowerCase())
    );
    const s = q.trim().toLowerCase();
    if (!s) return [...pinnedOptions, ...searchableOptions];

    const matchedOptions = searchableOptions.filter((x) =>
      x.name.toLowerCase().includes(s)
    );

    return [...pinnedOptions, ...matchedOptions];
  }, [alwaysVisibleOptionIds, q, options]);

  return (
    <View style={[styles.ddWrap, isOpen ? styles.ddWrapOpen : null]}>
      <Text style={styles.ddLabel}>
        {label} {required ? <Text style={styles.ddReq}>*</Text> : null}
      </Text>

      <Pressable
        onPress={() => !disabled && onToggle()}
        style={[
          styles.ddField,
          disabled ? styles.ddFieldDisabled : null,
          isOpen ? styles.ddFieldOpen : null,
        ]}
      >
        <Text style={[styles.ddValue, !valueLabel ? styles.ddPlaceholder : null]}>
          {valueLabel || placeholder}
        </Text>
        <Text style={styles.ddChevron}>{isOpen ? "˄" : "˅"}</Text>
      </Pressable>

      {isOpen ? (
        <View style={styles.ddFloatCard} pointerEvents="box-none">
          <View style={styles.ddListCard}>
            <View style={styles.ddSearchWrap}>
              <Input
                placeholder={searchPlaceholder || "Search..."}
                value={q}
                onChangeText={setQ}
                style={styles.ddSearchInput}
              />
            </View>

            <ScrollView
              style={styles.ddScroll}
              nestedScrollEnabled
              showsVerticalScrollIndicator
              keyboardShouldPersistTaps="handled"
            >
              {filtered.length === 0 ? (
                <Text style={styles.ddEmpty}>No items</Text>
              ) : (
                filtered.map((opt) => {
                  const selected = !!valueId && String(valueId) === String(opt.id);
                  return (
                    <Pressable
                      key={opt.id}
                      style={[styles.ddItem, selected ? styles.ddItemSelected : null]}
                      onPress={() => onSelect(opt)}
                    >
                      <View style={styles.ddRadioRow}>
                        <View style={[styles.ddRadio, selected ? styles.ddRadioOn : null]} />
                        <Text
                          style={[
                            styles.ddItemText,
                            selected ? styles.ddItemTextSelected : null,
                          ]}
                        >
                          {opt.name}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })
              )}
            </ScrollView>
          </View>
        </View>
      ) : null}
    </View>
  );
}

function onlyDigits(s: string) {
  return (s || "").replace(/\D/g, "");
}

function maskMobile(mobile?: string) {
  const m = onlyDigits(mobile || "");
  if (!m) return "";
  if (m.length <= 2) return "XXXXXXXXXX";
  return `XXXXXXXX${m.slice(-2)}`;
}

function maskEmail(email?: string) {
  const e = (email || "").trim();
  if (!e || !e.includes("@")) return "";
  const [u, d] = e.split("@");
  const userMasked =
    u.length <= 2
      ? `${u[0] || "*"}*`
      : `${u[0]}${"*".repeat(Math.max(3, u.length - 2))}${u[u.length - 1]}`;
  const domainParts = d.split(".");
  const domainName = domainParts[0] || "";
  const tld = domainParts.slice(1).join(".") || "com";
  const domainMasked =
    domainName.length <= 2
      ? `${domainName[0] || "*"}*`
      : `${domainName[0]}${"*".repeat(Math.max(3, domainName.length - 2))}${domainName[domainName.length - 1]
      }`;
  return `${userMasked}@${domainMasked}.${tld}`;
}

export function EditProfileScreen() {
  const navigation = useNavigation<Nav>();
  const { user, logout, setUser } = useAuth();

  const [loading, setLoading] = useState(false);

  // ---- Form fields (email is display-only, not editable) ----
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    dob: "",
    email: "",
    profession: "",
    addressLine1: "",
    addressLine2: "",
    cityName: "",
    familyDetail: "",
  });

  const setField = (k: keyof typeof form, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const [emailVerified, setEmailVerified] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [verificationParameter, setVerificationParameter] =
    useState<VerificationParameterModel>(EMPTY_VERIFICATION_PARAMETER);
  // ---- dropdown selections (ids) ----
  const [stateId, setStateId] = useState<string | null>(null);
  const [cityId, setCityId] = useState<string | null>(null);
  const [maritalStatusId, setMaritalStatusId] = useState<string | null>(null);

  // Gender stored as VALUE string
  const [genderValue, setGenderValue] = useState<string | null>(null);

  // ---- dropdown labels ----
  const [stateName, setStateName] = useState("");
  const [cityLabel, setCityLabel] = useState("");
  const [maritalLabel, setMaritalLabel] = useState("");
  const [genderLabel, setGenderLabel] = useState("");

  // ---- dropdown open flags ----
  const [openGender, setOpenGender] = useState(false);
  const [openMarital, setOpenMarital] = useState(false);
  const [openState, setOpenState] = useState(false);
  const [openCity, setOpenCity] = useState(false);

  // ---- options ----
  const [states, setStates] = useState<SelectOption[]>([]);
  const [cities, setCities] = useState<SelectOption[]>([]);
  const [maritals, setMaritals] = useState<SelectOption[]>([]);
  const [genders, setGenders] = useState<SelectOption[]>([]);

  // ---- loading flags ----
  const [loadingState, setLoadingState] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);
  const [loadingMarital, setLoadingMarital] = useState(false);
  const [loadingGender, setLoadingGender] = useState(false);

  // =======================
  // ✅ CHANGE MOBILE (3 steps)
  // Step 1: verify current mobile (enter current mobile → send OTP to OLD number)
  // Step 2: enter OLD OTP + New mobile → call changePhone(ChangePhoneDto {Otp, NewMobileNumber})
  // Step 3: enter NEW OTP + agree → call verifyNewMobileOtp(OtpVerificationNewNumDto {NewMobileNumber, NewOtp})
  // =======================
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [mobileStep, setMobileStep] = useState<1 | 2 | 3>(1);

  // step1: current mobile confirm input
  const [curMobileInput, setCurMobileInput] = useState("");

  // step2: old OTP + new mobile
  const [oldOtp, setOldOtp] = useState("");
  const [newMobile, setNewMobile] = useState("");

  // step3: new OTP + checkbox
  const [newOtp, setNewOtp] = useState("");
  const [agree, setAgree] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // =======================
  // ✅ REMOVE MY DATA (2 steps)
  // Step 1: confirm account removal
  // Step 2: send OTP by logged-in token and verify with a single OTP box
  // =======================
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteOtpInfo, setDeleteOtpInfo] = useState<OtpChannelInfoModel>(EMPTY_OTP_CHANNELS);

  // EMAIL VERIFICATION
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");

  // CHANGE EMAIL
  const [showVerifyCurrentEmailModal, setShowVerifyCurrentEmailModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [changeEmailOtp, setChangeEmailOtp] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const [showEnterCurrentEmailModal, setShowEnterCurrentEmailModal] = useState(false);
  const [currentEmailInput, setCurrentEmailInput] = useState("");

  const OTHER_CITY_OPTION: SelectOption = useMemo(
    () => ({ id: OTHER_CITY_ID, name: "Other" }),
    []
  );

  const closeAllDropdowns = () => {
    setOpenGender(false);
    setOpenMarital(false);
    setOpenState(false);
    setOpenCity(false);
  };

  const isOtherCity = useMemo(
    () => !!cityId && String(cityId).toLowerCase() === OTHER_CITY_ID.toLowerCase(),
    [cityId]
  );

  const maskedMobile = useMemo(() => maskMobile(form.mobileNumber), [form.mobileNumber]);
  const maskedEmail = useMemo(() => maskEmail(form.email), [form.email]);
  const showMobileVerificationUi = Boolean(verificationParameter.mobile);
  const showEmailVerificationUi = Boolean(verificationParameter.email);
  const [dobError, setDobError] = useState("");

  const syncDobField = (value: string) => {
    const formattedValue = formatDobInput(value);
    const validationError = formattedValue.length === 10 ? getDobValidationError(formattedValue) : "";

    setField("dob", formattedValue);
    setDobError(formattedValue.length === 10 ? validationError : "");
  };

  const validateDobField = () => {
    const validationError = getDobValidationError(form.dob);
    setDobError(validationError);
    return !validationError;
  };

  useEffect(() => {
    if (!genderValue || genders.length === 0) return;

    const g = genders.find(x => x.id === genderValue);
    if (g) setGenderLabel(g.name);

  }, [genderValue, genders]);

  useEffect(() => {
    if (!maritalStatusId || maritals.length === 0) return;

    const m = maritals.find(x => x.id === maritalStatusId);
    if (m) setMaritalLabel(m.name);

  }, [maritalStatusId, maritals]);

  useEffect(() => {
    if (!stateId || states.length === 0) return;

    const s = states.find(x => x.id === stateId);
    if (s) setStateName(s.name);

  }, [stateId, states]);


  useEffect(() => {
    if (!cityId || cities.length === 0) return;

    const c = cities.find(x => x.id === cityId);
    if (c) setCityLabel(c.name);

  }, [cityId, cities]);
  // ✅ Prefill from Auth user
  useEffect(() => {
    if (!user) return;

    loadUser();

  }, [user]);

  // ✅ Load State + Marital + Gender
  useEffect(() => {
    (async () => {
      try {
        setLoadingState(true);
        const res = await UserRegistrationService.getStates();
        const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
          id: String(x.id ?? x.stateId ?? x.value ?? ""),
          name: String(x.name ?? x.stateName ?? x.label ?? ""),
        }));
        setStates(list.filter((x) => x.id && x.name));
      } catch {
        setStates([]);
      } finally {
        setLoadingState(false);
      }
    })();

    (async () => {
      try {
        setLoadingMarital(true);
        const res = await UserRegistrationService.getEnumeration("MaritalStatus");
        const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
          id: String(x.enumerationId ?? x.id ?? ""),
          name: String(x.value ?? x.name ?? ""),
        }));
        setMaritals(list.filter((x) => x.id && x.name));
      } catch {
        setMaritals([]);
      } finally {
        setLoadingMarital(false);
      }
    })();

    (async () => {
      try {
        setLoadingGender(true);
        const res = await UserRegistrationService.getEnumeration("Gender");
        const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
          id: String(x.enumerationId ?? x.id ?? ""),
          name: String(x.value ?? x.name ?? ""),
        }));
        setGenders(list.filter((x) => x.id && x.name));
      } catch {
        setGenders([]);
      } finally {
        setLoadingGender(false);
      }
    })();
  }, []);

  //----load user data----
  const loadUser = async () => {
    try {
      setLoading(true);

      const res = await UserService.getUser();

      if (res?.status !== 200) return;

      const u = res.data ?? {};
      setForm({
        firstName: u.firstName ?? u.FirstName ?? "",
        lastName: u.lastName ?? u.LastName ?? "",
        mobileNumber: u.mobileNumber ?? u.MobileNumber ?? "",
        dob: (u.dob ?? u.Dob) ? format(new Date(u.dob ?? u.Dob), "dd-MM-yyyy") : "",
        email: u.email ?? u.Email ?? "",
        profession: u.profession ?? u.Profession ?? "",
        addressLine1: u.addressLine1 ?? u.AddressLine1 ?? "",
        addressLine2: u.addressLine2 ?? u.AddressLine2 ?? "",
        cityName: u.cityName ?? u.CityName ?? "",
        familyDetail: u.familyDetail ?? u.FamilyDetail ?? "",
      });

      setEmailVerified(Boolean(u?.isEmailVerified ?? u?.IsEmailVerified));
      setMobileVerified(Boolean(u?.isMobileVerified ?? u?.IsMobileVerified));
      setVerificationParameter(
        normalizeVerificationParameter(u?.verificationParameter ?? u?.VerificationParameter)
      );
      setDobError("");

      setGenderValue(u.gender ?? u.Gender ?? null);
      // setGenderLabel(u.gender ?? "");

      setMaritalStatusId(u.maritalStatus ?? u.MaritalStatus ?? null);

      setStateId(u.stateId ?? u.StateId ?? null);
      setCityId(u.cityId ?? u.CityId ?? null);

    } catch (e) {
      Flash.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Load cities by state + append Other
  const loadCitiesByState = async (sid: string) => {
    setLoadingCity(true);
    try {
      const res = await UserRegistrationService.getCitiesByState(sid);
      const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
        id: String(x.cityId ?? x.id ?? ""),
        name: String(x.cityName ?? x.name ?? ""),
      }));

      const cleaned = list.filter((x) => x.id && x.name);
      const hasOther = cleaned.some(
        (x) => String(x.id).toLowerCase() === OTHER_CITY_ID.toLowerCase()
      );

      setCities(hasOther ? cleaned : [...cleaned, OTHER_CITY_OPTION]);
    } catch {
      setCities([OTHER_CITY_OPTION]);
    } finally {
      setLoadingCity(false);
    }
  };

  useEffect(() => {
    if (stateId) loadCitiesByState(stateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId]);

  const isFormValid = useMemo(() => {
    const firstOk = !!form.firstName.trim();
    const lastOk = !!form.lastName.trim();
    const profOk = !!form.profession.trim();

    const genderOk = !!genderValue;
    const maritalOk = !!maritalStatusId;

    const addrOk = !!form.addressLine1.trim();
    const stateOk = !!stateId;
    const cityOk = !!cityId;
    const otherCityOk = !isOtherCity || !!form.cityName.trim();
    const dobOk = !!form.dob.trim() && !dobError && !!parseDobInput(form.dob);

    return (
      firstOk &&
      lastOk &&
      dobOk &&
      profOk &&
      genderOk &&
      maritalOk &&
      addrOk &&
      stateOk &&
      cityOk &&
      otherCityOk
    );
  }, [
    form.firstName,
    form.lastName,
    form.profession,
    genderValue,
    maritalStatusId,
    form.addressLine1,
    stateId,
    cityId,
    isOtherCity,
    form.cityName,
    form.dob,
    dobError,
  ]);

  // ✅ SAVE PROFILE
  const handleSave = async () => {
    closeAllDropdowns();
    const isDobValid = validateDobField();
    if (!isFormValid || !isDobValid) return Flash.error("Please fill required fields.");

    const parsedDob = parseDobInput(form.dob);
    if (!parsedDob) return Flash.error("Please enter a valid Date of Birth.");

    const payload = {
      firstName: form.firstName.trim() || null,
      lastName: form.lastName.trim() || null,
      dob: format(parsedDob, "yyyy-MM-dd"),
      profession: form.profession.trim() || null,
      gender: genderValue || null,
      maritalStatus: maritalStatusId || null,
      addressLine1: form.addressLine1.trim() || null,
      addressLine2: form.addressLine2.trim() || null,
      stateId: stateId || null,
      cityId: cityId || null,

      cityName: isOtherCity ? form.cityName.trim() || null : null,  // ✅

      familyDetail: form.familyDetail.trim() || null
    };

    setLoading(true);
    try {
      const res = await UserRegistrationService.updateProfile(payload);
      if (res?.status !== 200) return Flash.error(res?.message ?? "Profile update failed.");

      await setUser({
        ...(user || {}),
        name: `${payload.firstName} ${payload.lastName}`.trim(),
        mobileNumber: form.mobileNumber.trim(),
        raw: { ...(user?.raw ?? {}), ...(res?.data ?? payload) },
      });

      Flash.success(
        "Hare Krishna!",
        "Your profile has been updated successfully. May your journey remain steady and joyful in devotion."
      );
      navigation.goBack();
    } catch (e: any) {
      Flash.error(e?.message ?? "Update profile failed.");
    } finally {
      setLoading(false);
    }
  };

  // =======================
  // ✅ CHANGE MOBILE FLOW
  // =======================
  const openMobileChange = () => {
    setShowMobileModal(true);
    setMobileStep(1);
    setCurMobileInput("");
    setOldOtp("");
    setNewMobile("");
    setNewOtp("");
    setAgree(false);
  };

  const closeMobileChange = () => setShowMobileModal(false);

  // Step-1: verify current mobile matches & send OTP to OLD number
  const handleSendOtpToCurrent = async () => {
    const current = onlyDigits(form.mobileNumber);
    const entered = onlyDigits(curMobileInput);

    if (entered.length !== 10) return Flash.error("Enter current 10 digit number");
    if (entered !== current) return Flash.error("Current mobile number does not match");

    setLoading(true);
    try {
      const res = await ForgotMPINService.sendOtp({ mobileNumber: current });
      if (res?.status !== 200) return Flash.error(res?.message ?? "Failed to send OTP");

      Flash.success("Success!", "OTP sent to your current mobile");
      setMobileStep(2);
    } catch (e: any) {
      Flash.error(e?.message ?? "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step-2: OLD OTP + new mobile → call changePhone(ChangePhoneDto {Otp, NewMobileNumber})
  const handleSendOtpToNew = async () => {
    const nm = onlyDigits(newMobile);
    const otpOld = onlyDigits(oldOtp);

    if (otpOld.length !== 4) return Flash.error("Enter 4-digit OTP");
    if (nm.length !== 10) return Flash.error("Enter new 10 digit mobile");

    setLoading(true);
    try {
      // ✅ backend validates old OTP and sends OTP to NEW mobile
      const res = await UserService.changePhone({
        otp: otpOld,
        newMobileNumber: nm,
      });

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Failed to verify OTP / change phone");
        return;
      }

      Flash.success("Success!", "OTP sent to new mobile");
      setMobileStep(3);
    } catch (e: any) {
      Flash.error(e?.message ?? "Failed to verify OTP / send OTP to new mobile");
    } finally {
      setLoading(false);
    }
  };

  // Step-3: NEW OTP + agree → call verifyNewMobileOtp(OtpVerificationNewNumDto {NewMobileNumber, NewOtp})
  const handleVerifyAndUpdateMobile = async () => {
    const nm = onlyDigits(newMobile);
    const otpNew = onlyDigits(newOtp);

    if (nm.length !== 10) return Flash.error("Enter new 10 digit mobile");
    if (otpNew.length !== 4) return Flash.error("Enter 4-digit OTP");
    if (!agree) return Flash.error("Please accept Terms & Conditions");

    setLoading(true);
    try {
      const res = await UserService.otpVerificationNewNum({
        newMobileNumber: nm,
        otp: otpNew,
      });

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Invalid OTP");
        return;
      }

      Flash.success("Success!", "Mobile updated. Please login again.");
      closeMobileChange();

      await logout();
    } catch (e: any) {
      Flash.error(e?.message ?? "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  // =======================
  // ✅ REMOVE MY DATA FLOW
  // =======================
  const openDelete = () => {
    setShowDeleteModal(true);
    setDeleteStep(1);
    setDeleteOtp("");
    setDeleteOtpInfo(EMPTY_OTP_CHANNELS);
  };

  const closeDelete = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setDeleteOtp("");
    setDeleteOtpInfo(EMPTY_OTP_CHANNELS);
  };

  const getResolvedApiMessage = (response?: any, fallbackMessage?: string) => {
    return getBackendDisplayMessage(
      response?.data?.message,
      response?.data?.Message,
      response?.Data?.message,
      response?.Data?.Message,
      response?.Message,
      response?.message,
      fallbackMessage
    );
  };

  const getRemoveAccountMessage = (status?: number, messageKey?: string, response?: any) => {
    const resolvedMessage = getResolvedApiMessage(response, messageKey);

    if (status === 440 || messageKey === "SessionTimeOut") {
      return "Session expired. Please login again.";
    }

    if (messageKey === "IncorrectOTP") {
      return "Incorrect OTP. Please try again.";
    }

    if (messageKey === "OTPExpired") {
      return "OTP expired. Please request a new OTP.";
    }

    if (messageKey === "OTPSentSuccessfully") {
      return resolvedMessage ?? "OTP sent successfully.";
    }

    return resolvedMessage ?? "Delete failed";
  };

  const getRemoveAccountError = (error: any) => {
    const response = error?.response?.data ?? error;

    return {
      status: response?.status ?? response?.Status,
      message: getResolvedApiMessage(response, error?.message),
      messageKey: response?.messageKey ?? getBackendMessageKey(
        response?.messageKey,
        response?.message,
        response?.Message,
        error?.messageKey
      ),
      response,
    };
  };

  const handleDeleteSendOtp = async () => {
    setLoading(true);
    try {
      const res = await UserService.sendOtpByCode();
      const otpInfo = normalizeOtpChannelInfo(res?.data);

      if (res?.status === 440) {
        Flash.error(getRemoveAccountMessage(res?.status, res?.messageKey ?? res?.message, res));
        closeDelete();
        await logout({ deleteDevice: false });
        return;
      }

      if (res?.status !== 200) {
        return Flash.error(getRemoveAccountMessage(res?.status, res?.messageKey ?? res?.message, res));
      }

      if (!hasAnyVerificationChannel(otpInfo)) {
        return Flash.error("No verification channel is available for account deletion.");
      }

      setDeleteOtp("");
      setDeleteOtpInfo(otpInfo);
      setDeleteStep(2);
      Flash.success("Success!", getRemoveAccountMessage(res?.status, res?.messageKey ?? res?.message, res));
    } catch (e: any) {
      const apiError = getRemoveAccountError(e);

      if (apiError.status === 440) {
        Flash.error(getRemoveAccountMessage(apiError.status, apiError.messageKey, apiError.response));
        closeDelete();
        await logout({ deleteDevice: false });
        return;
      }

      Flash.error(getRemoveAccountMessage(apiError.status, apiError.messageKey, apiError.response));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    const otp = onlyDigits(deleteOtp);
    if (otp.length !== OTP_MAX_LENGTH) {
      return Flash.error(`Enter ${OTP_MAX_LENGTH}-digit OTP`);
    }

    setLoading(true);
    try {
      const res = await UserService.removeMyData(otp);

      if (res?.status === 440) {
        Flash.error(getRemoveAccountMessage(res?.status, res?.messageKey ?? res?.message, res));
        closeDelete();
        await logout({ deleteDevice: false });
        return;
      }

      if (res?.status != 200) {
        return Flash.error(getRemoveAccountMessage(res?.status, res?.messageKey ?? res?.message, res));
      }

      Flash.success("Success!", getRemoveAccountMessage(res?.status, res?.messageKey ?? res?.message, res));
      closeDelete();

      await logout({ deleteDevice: false });
    } catch (e: any) {
      const apiError = getRemoveAccountError(e);

      if (apiError.status === 440) {
        Flash.error(getRemoveAccountMessage(apiError.status, apiError.messageKey, apiError.response));
        closeDelete();
        await logout({ deleteDevice: false });
        return;
      }

      Flash.error(getRemoveAccountMessage(apiError.status, apiError.messageKey, apiError.response));
    } finally {
      setLoading(false);
    }
  };

  // SEND EMAIL OTP
  const handleSendEmailOtp = async () => {
    try {
      setLoading(true);

      const res = await UserService.sendEmailOtp(form.email);

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Failed to send OTP");
        return;
      }

      setShowVerifyEmailModal(true);
      Flash.success("Success!", "OTP sent to your email");

    } catch (e: any) {
      Flash.error(e?.message ?? "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // VERIFY EMAIL OTP
  const handleVerifyEmail = async () => {
    if (!emailOtp) return Flash.error("Enter OTP");

    try {
      setLoading(true);

      const res = await UserService.emailVerification(emailOtp);

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Invalid OTP");
        return;
      }

      Flash.success("Success!", "Email verified successfully");

      setShowVerifyEmailModal(false);

      await loadUser(); // reload profile

    } catch (e: any) {
      Flash.error(e?.message ?? "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // STEP 1 VERIFY CURRENT EMAIL
  const openChangeEmail = () => {
    setCurrentEmailInput("");
    setShowEnterCurrentEmailModal(true);
  };

  // STEP 2 VERIFY CURRENT EMAIL OTP
  const handleVerifyCurrentEmailOtp = async () => {
    if (!changeEmailOtp) return Flash.error("Enter OTP");

    try {
      const res = await UserService.emailVerification(changeEmailOtp);

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Invalid OTP");
        return;
      }

      setShowVerifyCurrentEmailModal(false);
      setShowChangeEmailModal(true);

    } catch (e: any) {
      Flash.error("Verification failed");
    }
  };

  const handleVerifyCurrentEmail = async () => {

    if (!currentEmailInput) {
      return Flash.error("Enter current email");
    }

    if (currentEmailInput.trim().toLowerCase() !== form.email.toLowerCase()) {
      return Flash.error("Current email does not match");
    }

    try {

      setLoading(true);

      const res = await UserService.sendEmailOtp(currentEmailInput);

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Failed to send OTP");
        return;
      }

      Flash.success("Success!", "OTP sent to your email");

      setShowEnterCurrentEmailModal(false);
      setShowChangeEmailModal(true);

    } catch (e: any) {

      Flash.error(e?.message ?? "Failed");

    } finally {

      setLoading(false);

    }
  };

  // STEP 3 CHANGE EMAIL
  const handleChangeEmail = async () => {
    if (!changeEmailOtp || !newEmail) {
      return Flash.error("Enter OTP and new email");
    }

    try {
      setLoading(true);

      const res = await UserService.changeEmail({
        otp: changeEmailOtp,
        email: newEmail
      });

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Change email failed");
        return;
      }

      Flash.success("Success!", "Email changed successfully");

      setShowChangeEmailModal(false);

      await loadUser();

    } catch (e: any) {
      Flash.error(e?.message ?? "Failed");
    } finally {
      setLoading(false);
    }
  };

  // ✅ LOGOUT
  const handleLogout = async () => {
    await logout();
  };

  const termsText = [
    {
      title: "Data Migration",
      body:
        "All your existing data, including sadhana records and profile information, will be linked to your new mobile number.",
    },
    {
      title: "Access Revocation",
      body:
        "You will no longer be able to access your account or data using your old mobile number once this change is verified.",
    },
    {
      title: "Communication Channel",
      body:
        "All future communications, including OTPs and notifications, will be sent exclusively to your new mobile number.",
    },
    {
      title: "Session Termination",
      body:
        "Upon successful update, your current session will be terminated, and you will be required to log in again with your new number.",
    },
    {
      title: "Security Verification",
      body:
        "This action is irreversible without going through the support process. Ensure you have access to the new number permanently.",
    },
  ];

  if (!user) return null;

  return (
    <DivineBackground overlayColor="#FFF8E7" overlayOpacity={0.88}>
      <Header title="Edit Profile" showBack showProfile={false} showUserName={false} />

      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
          <View style={styles.card}>
            {/* ✅ Mobile */}
            <View style={styles.topRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.topLabel}>MOBILE NUMBER</Text>
                <Text style={styles.topValue}>{maskedMobile}</Text>

                {showMobileVerificationUi ? (
                  <View style={[styles.badge, mobileVerified ? styles.badgeOk : styles.badgeWarn]}>
                    <Text style={[styles.badgeText, mobileVerified ? styles.badgeTextOk : styles.badgeTextWarn]}>
                      {mobileVerified ? "Verified" : "Not Verified"}
                    </Text>
                  </View>
                ) : null}

                {verificationParameter.mobile && !mobileVerified ? (
                  <Text style={styles.verifyPendingText}>Verification pending</Text>
                ) : null}
              </View>

              {showMobileVerificationUi ? (
                <Pressable onPress={openMobileChange} style={styles.changeBtn}>
                  <Text style={styles.changeText}>Change</Text>
                </Pressable>
              ) : null}
            </View>

            {/* ✅ Email (display-only) */}
            <View style={styles.topRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.topLabel}>EMAIL ADDRESS</Text>
                <Text style={styles.topValue}>{maskedEmail || "-"}</Text>

                {showEmailVerificationUi ? (
                  <View style={[
                    styles.badge,
                    emailVerified ? styles.badgeOk : styles.badgeWarn
                  ]}>
                    <Text style={[
                      styles.badgeText,
                      emailVerified ? styles.badgeTextOk : styles.badgeTextWarn
                    ]}>
                      {emailVerified ? "Verified" : "Not Verified"}
                    </Text>
                  </View>
                ) : null}

                {verificationParameter.email && !emailVerified && (
                  <Pressable onPress={handleSendEmailOtp}>
                    <Text style={styles.verifyLinkText}>
                      Verify Now
                    </Text>
                  </Pressable>
                )}
              </View>

              {showEmailVerificationUi ? (
                <Pressable onPress={openChangeEmail} style={styles.changeBtn}>
                  <Text style={styles.changeText}>Change</Text>
                </Pressable>
              ) : null}
            </View>

            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Personal Details</Text>
            </View>

            {/* First / Last */}
            {/* <View style={styles.row2}>
              <View style={{ flex: 1 }}> */}
            <Input
              label="First Name *"
              placeholder="First Name"
              value={form.firstName}
              onChangeText={(t) => setField("firstName", t)}
              onFocus={closeAllDropdowns}
            />

            <Input
              label="Last Name *"
              placeholder="Last Name"
              value={form.lastName}
              onChangeText={(t) => setField("lastName", t)}
              onFocus={closeAllDropdowns}
            />

            <View>
              <Input
                label="Date of Birth *"
                placeholder={DOB_PLACEHOLDER}
                keyboardType="number-pad"
                maxLength={10}
                value={form.dob}
                onChangeText={syncDobField}
                onFocus={closeAllDropdowns}
                onBlur={validateDobField}
              />
              {dobError ? <Text style={styles.errorText}>{dobError}</Text> : null}
            </View>

            <Input
              label="Profession *"
              placeholder="e.g. Engineer, Doctor"
              value={form.profession}
              onChangeText={(t) => setField("profession", t)}
              onFocus={closeAllDropdowns}
            />

            <SearchSelect
              label="Gender"
              required
              placeholder={loadingGender ? "Loading..." : "Select Gender"}
              valueLabel={genderLabel}
              valueId={genderValue ? genderValue : null}
              options={genders}
              disabled={loadingGender}
              isOpen={openGender}
              onToggle={() => {
                setOpenGender((p) => !p);
                setOpenMarital(false);
                setOpenState(false);
                setOpenCity(false);
              }}
              onSelect={(opt) => {
                closeAllDropdowns();
                setGenderLabel(opt.name);
                setGenderValue(opt.id);
              }}
              searchPlaceholder="Search Gender..."
            />

            {/* Marital */}
            <SearchSelect
              label="Marital Status"
              required
              placeholder={loadingMarital ? "Loading..." : "Select Status"}
              valueLabel={maritalLabel}
              valueId={maritalStatusId}
              options={maritals}
              disabled={loadingMarital}
              isOpen={openMarital}
              onToggle={() => {
                setOpenMarital((p) => !p);
                setOpenGender(false);
                setOpenState(false);
                setOpenCity(false);
              }}
              onSelect={(opt) => {
                closeAllDropdowns();
                setMaritalStatusId(opt.id);
                setMaritalLabel(opt.name);
              }}
              searchPlaceholder="Search Status..."
            />

            {/* Address section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Address Details</Text>

              <Input
                label="Address Line 1 *"
                placeholder="House No, Building, Street"
                value={form.addressLine1}
                onChangeText={(t) => setField("addressLine1", t)}
                onFocus={closeAllDropdowns}
              />

              <Input
                label="Address Line 2"
                placeholder="Area, Landmark (Optional)"
                value={form.addressLine2}
                onChangeText={(t) => setField("addressLine2", t)}
                onFocus={closeAllDropdowns}
              />

              {/* <View style={styles.row2}>
                    <View style={{ flex: 1 }}> */}
              <SearchSelect
                label="State"
                required
                placeholder={loadingState ? "Loading..." : "Select State"}
                valueLabel={stateName}
                valueId={stateId}
                options={states}
                disabled={loadingState}
                isOpen={openState}
                onToggle={() => {
                  setOpenState((p) => !p);
                  setOpenCity(false);
                  setOpenGender(false);
                  setOpenMarital(false);
                }}
                onSelect={async (opt) => {
                  closeAllDropdowns();
                  setStateId(opt.id);
                  setStateName(opt.name);

                  setCityId(null);
                  setCityLabel("");
                  setCities([]);
                  setField("cityName", "");

                  await loadCitiesByState(opt.id);
                }}
                searchPlaceholder="Search State..."
              />
              {/* </View> */}

              {/* <View style={{ flex: 1 }}> */}
              <SearchSelect
                label="City"
                required
                placeholder={!stateId ? "Select State First" : loadingCity ? "Loading..." : "Select City"}
                valueLabel={cityLabel}
                valueId={cityId}
                options={cities}
                alwaysVisibleOptionIds={[OTHER_CITY_ID]}
                disabled={!stateId || loadingCity}
                isOpen={openCity}
                onToggle={() => {
                  if (!stateId || loadingCity) return;
                  setOpenCity((p) => !p);
                  setOpenState(false);
                  setOpenGender(false);
                  setOpenMarital(false);
                }}
                onSelect={(opt) => {
                  closeAllDropdowns();
                  setCityId(opt.id);
                  setCityLabel(opt.name);

                  const other = String(opt.id).toLowerCase() === OTHER_CITY_ID.toLowerCase();
                  if (!other) setField("cityName", "");
                }}
                searchPlaceholder="Search City..."
              />
              {/* </View> */}
              {/* </View> */}

              {isOtherCity ? (
                <Input
                  label="City Name *"
                  placeholder="Enter your city name"
                  value={form.cityName}
                  onChangeText={(t) => setField("cityName", t)}
                  onFocus={closeAllDropdowns}
                />
              ) : null}

              <Input
                label="Family Details (Optional)"
                placeholder="Brief details about family members"
                value={form.familyDetail}
                onChangeText={(t) => setField("familyDetail", t)}
                onFocus={closeAllDropdowns}
                multiline
                numberOfLines={4}
                style={{ minHeight: 90, textAlignVertical: "top", paddingVertical: 10 }}
              />
            </View>

            <Button
              title={loading ? "Saving..." : "Save Changes"}
              onPress={handleSave}
              disabled={loading || !isFormValid}
              style={{ marginTop: 6, opacity: loading || !isFormValid ? 0.6 : 1 }}
            />

            <Pressable onPress={handleLogout} style={styles.logoutBtn}>
              <Text style={styles.logoutText}>↩️ Logout</Text>
            </Pressable>

            <Pressable onPress={openDelete} style={styles.removeBtn}>
              <Text style={styles.removeText}>🗑️ Remove My Data</Text>
            </Pressable>
          </View>
        </ScrollView>

        {/* ✅ Change Mobile Modal */}
        <Modal visible={showMobileModal} transparent animationType="fade" onRequestClose={closeMobileChange}>
          <Pressable style={styles.modalBackdrop} onPress={closeMobileChange} />
          <ToastHost />
          <View style={styles.modalCenter}>
            <View style={styles.modalCard}>
              <Text style={styles.modalTitle}>Update Mobile Number</Text>
              <Text style={styles.modalSub}>
                Changing your mobile number will require you to login again with the new number.
              </Text>

              {/* Step 1 */}
              {mobileStep === 1 ? (
                <>
                  <Text style={styles.modalHint}>To secure your account, verify your current mobile number:</Text>

                  <Input
                    label={`Current Mobile (${maskMobile(form.mobileNumber)})`}
                    placeholder="Enter current 10 digit number"
                    keyboardType="number-pad"
                    maxLength={10}
                    value={curMobileInput}
                    onChangeText={(t) => setCurMobileInput(onlyDigits(t).slice(0, 10))}
                  />

                  <View style={styles.modalBtnRow}>
                    <Pressable onPress={closeMobileChange} style={styles.modalBtnOutline}>
                      <Text style={styles.modalBtnOutlineText}>Cancel</Text>
                    </Pressable>

                    <Pressable
                      onPress={handleSendOtpToCurrent}
                      disabled={loading || onlyDigits(curMobileInput).length !== 10}
                      style={[
                        styles.modalBtnPrimary,
                        loading || onlyDigits(curMobileInput).length !== 10 ? styles.btnDisabled : null,
                      ]}
                    >
                      <Text style={styles.modalBtnPrimaryText}>Send OTP</Text>
                    </Pressable>
                  </View>
                </>
              ) : null}

              {/* Step 2 */}
              {mobileStep === 2 ? (
                <>
                  <Text style={styles.otpGreen}>OTP Sent</Text>

                  <Input
                    label="Verification Code"
                    placeholder="Enter 4 digit OTP sent to old mobile"
                    keyboardType="number-pad"
                    maxLength={4}
                    value={oldOtp}
                    onChangeText={(t) => setOldOtp(onlyDigits(t).slice(0, 4))}
                  />

                  <Input
                    label="New Mobile Number"
                    placeholder="Enter new 10 digit number"
                    keyboardType="number-pad"
                    maxLength={10}
                    value={newMobile}
                    onChangeText={(t) => setNewMobile(onlyDigits(t).slice(0, 10))}
                  />

                  <View style={styles.modalBtnRow}>
                    <Pressable onPress={() => setMobileStep(1)} style={styles.modalBtnOutline}>
                      <Text style={styles.modalBtnOutlineText}>Back</Text>
                    </Pressable>

                    <Pressable
                      onPress={handleSendOtpToNew}
                      disabled={loading || onlyDigits(oldOtp).length !== 4 || onlyDigits(newMobile).length !== 10}
                      style={[
                        styles.modalBtnPrimary,
                        loading || onlyDigits(oldOtp).length !== 4 || onlyDigits(newMobile).length !== 10
                          ? styles.btnDisabled
                          : null,
                      ]}
                    >
                      <Text style={styles.modalBtnPrimaryText}>Verify & Send OTP</Text>
                    </Pressable>
                  </View>
                </>
              ) : null}

              {/* Step 3 */}
              {mobileStep === 3 ? (
                <>
                  <Text style={styles.otpGreen}>OTP Sent</Text>

                  <Input
                    label="Verification Code"
                    placeholder="Enter 4 digit OTP sent to new mobile"
                    keyboardType="number-pad"
                    maxLength={4}
                    value={newOtp}
                    onChangeText={(t) => setNewOtp(onlyDigits(t).slice(0, 4))}
                  />

                  <Pressable onPress={() => setAgree((p) => !p)} style={styles.checkRow}>
                    <View style={[styles.checkbox, agree ? styles.checkboxOn : null]}>
                      {agree ? <Text style={styles.checkboxTick}>✓</Text> : null}
                    </View>

                    <Text style={styles.checkText}>
                      I agree to the{" "}
                      <Text style={styles.link} onPress={() => setShowTermsModal(true)}>
                        Terms and Conditions
                      </Text>{" "}
                      for updating my mobile number.
                    </Text>
                  </Pressable>

                  <View style={styles.modalBtnRow}>
                    <Pressable onPress={() => setMobileStep(2)} style={styles.modalBtnOutline}>
                      <Text style={styles.modalBtnOutlineText}>Back</Text>
                    </Pressable>

                    <Pressable
                      onPress={handleVerifyAndUpdateMobile}
                      disabled={loading || onlyDigits(newOtp).length !== 4 || !agree}
                      style={[
                        styles.modalBtnPrimary,
                        loading || onlyDigits(newOtp).length !== 4 || !agree ? styles.btnDisabled : null,
                      ]}
                    >
                      <Text style={styles.modalBtnPrimaryText}>Verify & Update</Text>
                    </Pressable>
                  </View>
                </>
              ) : null}
            </View>
          </View>
        </Modal>

        {/* ✅ verify email Modal */}
        <Modal visible={showVerifyEmailModal} transparent animationType="fade">
          <Pressable style={styles.modalBackdrop} onPress={() => setShowVerifyEmailModal(false)} />
          <ToastHost />

          <View style={styles.modalCenter}>
            <View style={styles.modalCard}>

              <Text style={styles.modalTitle}>Verify Email</Text>

              <Input
                label="Verification Code"
                placeholder="Enter code"
                value={emailOtp}
                keyboardType="number-pad"
                maxLength={4}
                onChangeText={setEmailOtp}
              />

              <Pressable
                style={styles.modalBtnPrimarySingle}
                onPress={handleVerifyEmail}
              >
                <Text style={styles.modalBtnPrimaryText}>
                  Verify Email
                </Text>
              </Pressable>

            </View>
          </View>
        </Modal>

        {/* ✅ Verify Current Email Modal */}
        {/* <Modal visible={showVerifyCurrentEmailModal} transparent animationType="fade">
          <Pressable style={styles.modalBackdrop} onPress={() => setShowVerifyCurrentEmailModal(false)} />

          <View style={styles.modalCenter}>
            <View style={styles.modalCard}>

              <Text style={styles.modalTitle}>Change Email Address</Text>

              <Input
                label="Verification Code"
                placeholder="Enter code sent to old email"
                value={changeEmailOtp}
                keyboardType="number-pad"
                maxLength={4}
                onChangeText={setChangeEmailOtp}
              />

              <Pressable
                style={styles.modalBtnPrimarySingle}
                onPress={handleVerifyCurrentEmailOtp}
              >
                <Text style={styles.modalBtnPrimaryText}>
                  Next
                </Text>
              </Pressable>

            </View>
          </View>
        </Modal> */}

        {/* ✅ Enter Current Email Modal */}
        <Modal visible={showEnterCurrentEmailModal} transparent animationType="fade">

          <Pressable
            style={styles.modalBackdrop}
            onPress={() => setShowEnterCurrentEmailModal(false)}
          />
          <ToastHost />

          <View style={styles.modalCenter}>
            <View style={styles.modalCard}>

              <Text style={styles.modalTitle}>Change Email Address</Text>

              <Text style={styles.modalSub}>
                To secure your account, please verify your current email.
              </Text>

              <Input
                label={`Current Email (${maskedEmail})`}
                placeholder="Enter your current email"
                value={currentEmailInput}
                onChangeText={setCurrentEmailInput}
                autoCapitalize="none"
                keyboardType="email-address"
              />

              <Pressable
                style={styles.modalBtnPrimarySingle}
                onPress={handleVerifyCurrentEmail}
              >
                <Text style={styles.modalBtnPrimaryText}>
                  Next
                </Text>
              </Pressable>

            </View>
          </View>

        </Modal>

        {/* ✅ Change Email Modal */}
        <Modal visible={showChangeEmailModal} transparent animationType="fade">
          <Pressable style={styles.modalBackdrop} onPress={() => setShowChangeEmailModal(false)} />
          <ToastHost />

          <View style={styles.modalCenter}>
            <View style={styles.modalCard}>

              <Text style={styles.modalTitle}>Change Email Address</Text>

              <Input
                label="Verification Code"
                placeholder="Enter OTP"
                value={changeEmailOtp}
                keyboardType="number-pad"
                maxLength={4}
                onChangeText={setChangeEmailOtp}
              />

              <Input
                label="New Email Address"
                placeholder="Enter new email"
                value={newEmail}
                onChangeText={setNewEmail}
              />

              <Pressable
                style={styles.modalBtnPrimarySingle}
                onPress={handleChangeEmail}
              >
                <Text style={styles.modalBtnPrimaryText}>
                  Verify & Change
                </Text>
              </Pressable>

            </View>
          </View>
        </Modal>

        {/* ✅ Terms Modal */}
        <Modal visible={showTermsModal} transparent animationType="fade" onRequestClose={() => setShowTermsModal(false)}>
          <Pressable style={styles.modalBackdrop} onPress={() => setShowTermsModal(false)} />
          <ToastHost />
          <View style={styles.modalCenter}>
            <View style={[styles.modalCard, { paddingBottom: 14 }]}>
              <View style={styles.termsHeader}>
                <Text style={styles.modalTitle}>Terms & Conditions</Text>
                <Pressable onPress={() => setShowTermsModal(false)} style={styles.termsClose}>
                  <Text style={styles.modalCloseXText}>✕</Text>
                </Pressable>
              </View>

              <Text style={[styles.modalSub, { marginTop: -2 }]}>
                By changing your mobile number, you acknowledge and agree to the following:
              </Text>

              <View style={{ gap: 10, marginTop: 10 }}>
                {termsText.map((t) => (
                  <View key={t.title} style={{ gap: 3 }}>
                    <Text style={styles.termLine}>
                      • <Text style={styles.termTitle}>{t.title}:</Text> {t.body}
                    </Text>
                  </View>
                ))}
              </View>

              <Pressable
                onPress={() => {
                  setAgree(true);
                  setShowTermsModal(false);
                }}
                style={[styles.modalBtnPrimarySingle, { marginTop: 14 }]}
              >
                <Text style={styles.modalBtnPrimaryText}>I Understand & Agree</Text>
              </Pressable>
            </View>
          </View>
        </Modal>

        {/* ✅ Remove My Data Modal (2-step like screenshot) */}
        <Modal visible={showDeleteModal} transparent animationType="fade" onRequestClose={closeDelete}>
          <Pressable style={styles.modalBackdrop} onPress={closeDelete} />
          <ToastHost />
          <View style={styles.modalCenter}>
            <View style={[styles.modalCard, { borderColor: "rgba(239,68,68,0.25)" }]}>
              <Text style={[styles.modalTitle, { color: "rgba(239,68,68,0.95)" }]}>
                Delete Account Permanently
              </Text>
              <Text style={styles.modalSub}>
                This action cannot be undone. All your sadhana data will be lost.
              </Text>

              {deleteStep === 1 ? (
                <>
                  <Text style={styles.modalHint}>
                    Are you sure you want to permanently remove your account?
                  </Text>
                  <Text style={styles.deleteWarningText}>
                    Once deleted, your profile and sadhana data cannot be restored.
                  </Text>

                  <View style={styles.modalBtnRow}>
                    <Pressable onPress={closeDelete} style={styles.modalBtnOutline}>
                      <Text style={styles.modalBtnOutlineText}>Cancel</Text>
                    </Pressable>

                    <Pressable
                      onPress={handleDeleteSendOtp}
                      disabled={loading}
                      style={[
                        styles.modalBtnPrimary,
                        { backgroundColor: "rgba(239,68,68,0.95)" },
                        loading ? styles.btnDisabled : null,
                      ]}
                    >
                      <Text style={styles.modalBtnPrimaryText}>
                        {loading ? "Please wait..." : "Continue"}
                      </Text>
                    </Pressable>
                  </View>
                </>
              ) : (
                <>
                  <Text style={styles.modalHint}>
                    Enter the OTP sent to your enabled verification channel.
                  </Text>

                  {deleteOtpInfo.mobile ? (
                    <View style={styles.deleteChannelRow}>
                      <Text style={styles.deleteChannelLabel}>Mobile OTP</Text>
                      <Text style={styles.deleteChannelValue}>
                        {deleteOtpInfo.maskedMobileNumber ?? "Sent to registered mobile"}
                      </Text>
                    </View>
                  ) : null}

                  {deleteOtpInfo.email ? (
                    <View style={styles.deleteChannelRow}>
                      <Text style={styles.deleteChannelLabel}>Email OTP</Text>
                      <Text style={styles.deleteChannelValue}>
                        {deleteOtpInfo.maskedEmail ?? "Sent to registered email"}
                      </Text>
                    </View>
                  ) : null}

                  {deleteOtpInfo.mobile && deleteOtpInfo.email ? (
                    <Text style={styles.deleteHelpText}>
                      If you received different OTPs on email and mobile, you can enter either one below.
                    </Text>
                  ) : null}

                  <Input
                    label="OTP"
                    placeholder={OTP_PLACEHOLDER}
                    keyboardType="number-pad"
                    maxLength={OTP_MAX_LENGTH}
                    value={deleteOtp}
                    onChangeText={(t) => setDeleteOtp(onlyDigits(t).slice(0, OTP_MAX_LENGTH))}
                  />

                  <View style={styles.modalBtnRow}>
                    <Pressable onPress={() => setDeleteStep(1)} style={styles.modalBtnOutline}>
                      <Text style={styles.modalBtnOutlineText}>Back</Text>
                    </Pressable>

                    <Pressable
                      onPress={handleConfirmDelete}
                      disabled={loading || onlyDigits(deleteOtp).length !== OTP_MAX_LENGTH}
                      style={[
                        styles.modalBtnPrimary,
                        { backgroundColor: "rgba(239,68,68,0.95)" },
                        loading || onlyDigits(deleteOtp).length !== OTP_MAX_LENGTH ? styles.btnDisabled : null,
                      ]}
                    >
                      <Text style={styles.modalBtnPrimaryText}>
                        {loading ? "Deleting..." : "Verify & Delete"}
                      </Text>
                    </Pressable>
                  </View>
                </>
              )}
            </View>
          </View>
        </Modal>
      </KeyboardAvoidingView>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  scroll: { padding: 16, paddingBottom: 110, alignItems: "center" },

  card: {
    width: "100%",
    maxWidth: 430,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.20)",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 10 },
    elevation: 6,
    gap: 12,
  },

  row2: { flexDirection: "row", gap: 12 },

  topRow: {
    backgroundColor: "rgba(255,237,213,0.45)",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
    borderRadius: 14,
    padding: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },

  topLabel: {
    fontSize: 10,
    fontWeight: "900",
    color: "rgba(154,52,18,0.9)",
    letterSpacing: 0.8,
  },
  topValue: { marginTop: 2, fontSize: 14, fontWeight: "900", color: "#4A2311" },

  badge: {
    alignSelf: "flex-start",
    marginTop: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  badgeOk: {
    backgroundColor: "rgba(34,197,94,0.15)",
    borderWidth: 1,
    borderColor: "rgba(34,197,94,0.35)",
  },
  badgeWarn: {
    backgroundColor: "rgba(239,68,68,0.10)",
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.30)",
  },
  badgeText: { fontSize: 11, fontWeight: "900" },
  badgeTextOk: { color: "rgba(22,101,52,0.95)" },
  badgeTextWarn: { color: "rgba(185,28,28,0.95)" },
  verifyPendingText: {
    color: "rgba(74,35,17,0.55)",
    fontWeight: "800",
    marginTop: 10,
    fontSize: 12,
  },
  verifyLinkText: {
    color: "#2563EB",
    fontWeight: "900",
    marginTop: 10,
  },

  changeBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.35)",
    backgroundColor: "rgba(255,255,255,0.75)",
  },
  changeBtnGhost: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.25)",
    backgroundColor: "transparent",
    opacity: 0.6,
  },
  changeText: { fontSize: 12, fontWeight: "900", color: "#9A3412" },

  sectionHeader: { paddingTop: 6, paddingBottom: 2 },

  section: {
    gap: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
  },
  sectionTitle: { fontSize: 13, fontWeight: "900", color: "rgba(234,88,12,0.90)" },
  errorText: { fontSize: 12, fontWeight: "800", color: "rgba(239,68,68,0.95)", marginTop: 6 },

  // Dropdown
  ddWrap: { gap: 6, position: "relative", zIndex: 1 },
  ddWrapOpen: { zIndex: 999 },
  ddLabel: { fontSize: 12, fontWeight: "900", color: "#4A2311" },
  ddReq: { color: "rgba(239,68,68,0.95)" },

  ddField: {
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.28)",
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ddFieldOpen: { borderColor: "rgba(249,115,22,0.65)" },
  ddFieldDisabled: { opacity: 0.55 },

  ddValue: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.92)" },
  ddPlaceholder: { color: "rgba(107,114,128,0.85)" },
  ddChevron: { fontSize: 16, fontWeight: "900", color: "rgba(234,88,12,0.95)" },

  ddFloatCard: { position: "absolute", left: 0, right: 0, top: 74 },
  ddListCard: {
    backgroundColor: "#fff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.20)",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
  },
  ddSearchWrap: { padding: 10, borderBottomWidth: 1, borderBottomColor: "rgba(251,146,60,0.10)" },
  ddSearchInput: { marginTop: -6 },
  ddScroll: { maxHeight: 240 },

  ddItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(251,146,60,0.10)",
  },
  ddItemSelected: { backgroundColor: "rgba(255,237,213,0.65)" },

  ddRadioRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  ddRadio: {
    width: 14,
    height: 14,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: "rgba(234,88,12,0.55)",
    backgroundColor: "transparent",
  },
  ddRadioOn: { backgroundColor: "rgba(234,88,12,0.95)", borderColor: "rgba(234,88,12,0.95)" },

  ddItemText: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.92)" },
  ddItemTextSelected: { color: "rgba(124,45,18,0.95)", fontWeight: "900" },

  ddEmpty: { padding: 12, fontSize: 12, fontWeight: "800", color: "rgba(107,114,128,0.85)" },

  // bottom buttons
  logoutBtn: {
    marginTop: 6,
    backgroundColor: "rgba(255,255,255,0.85)",
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.20)",
    borderRadius: 14,
    paddingVertical: 12,
    alignItems: "center",
  },
  logoutText: { fontSize: 13, fontWeight: "900", color: "rgba(239,68,68,0.95)" },

  removeBtn: { paddingVertical: 10, alignItems: "center" },
  removeText: { fontSize: 13, fontWeight: "900", color: "rgba(185,28,28,0.95)" },

  // modals
  modalBackdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: "rgba(0,0,0,0.45)" },
  modalCenter: { flex: 1, justifyContent: "center", padding: 18 },
  modalCard: {
    backgroundColor: "rgba(255,255,255,0.96)",
    borderRadius: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.20)",
    shadowColor: "#000",
    shadowOpacity: 0.14,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 10 },
    elevation: 9,
    gap: 10,
  },
  modalTitle: { fontSize: 15, fontWeight: "900", color: "#4A2311" },
  modalSub: { fontSize: 12.5, fontWeight: "700", color: "rgba(74,35,17,0.55)" },
  modalHint: { fontSize: 12.5, fontWeight: "800", color: "rgba(74,35,17,0.70)" },
  deleteWarningText: {
    fontSize: 12,
    fontWeight: "700",
    color: "rgba(127,29,29,0.92)",
    lineHeight: 18,
  },
  deleteChannelRow: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: "rgba(254,242,242,0.9)",
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.18)",
    gap: 2,
  },
  deleteChannelLabel: {
    fontSize: 11,
    fontWeight: "900",
    color: "rgba(153,27,27,0.95)",
    letterSpacing: 0.4,
  },
  deleteChannelValue: {
    fontSize: 13,
    fontWeight: "800",
    color: "#4A2311",
  },
  deleteHelpText: {
    fontSize: 11.5,
    fontWeight: "700",
    color: "rgba(74,35,17,0.60)",
    lineHeight: 17,
  },
  otpGreen: { fontSize: 12.5, fontWeight: "900", color: "rgba(34,197,94,0.95)" },

  modalBtnRow: { flexDirection: "row", gap: 10, marginTop: 8 },

  modalBtnOutline: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.28)",
    paddingVertical: 12,
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
  },
  modalBtnOutlineText: { fontSize: 13, fontWeight: "900", color: "#9A3412" },

  modalBtnPrimary: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: "center",
    backgroundColor: "rgba(249,115,22,0.95)",
  },
  modalBtnPrimarySingle: {
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 18,
    alignItems: "center",
    backgroundColor: "rgba(249,115,22,0.95)",
  },
  modalBtnPrimaryText: { fontSize: 13, fontWeight: "900", color: "#fff" },

  btnDisabled: { opacity: 0.5 },

  // checkbox + terms
  checkRow: { flexDirection: "row", alignItems: "center", gap: 10, marginTop: 4 },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "rgba(156,163,175,0.8)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  checkboxOn: { backgroundColor: "rgba(249,115,22,0.95)", borderColor: "rgba(249,115,22,0.95)" },
  checkboxTick: { color: "#fff", fontSize: 12, fontWeight: "900" },
  checkText: { flex: 1, fontSize: 12.5, fontWeight: "800", color: "rgba(55,65,81,0.9)" },
  link: { color: "rgba(37,99,235,0.95)", fontWeight: "900" },

  // terms modal
  termsHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  termsClose: {
    width: 36,
    height: 36,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.9)",
  },
  modalCloseXText: { fontSize: 16, fontWeight: "900", color: "rgba(17,24,39,0.65)" },
  termLine: { fontSize: 12.5, fontWeight: "700", color: "rgba(31,41,55,0.88)", lineHeight: 18 },
  termTitle: { fontWeight: "900", color: "rgba(17,24,39,0.92)" },
});
